// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useContext } from 'react';
import styled from '@emotion/styled';
import Transition from 'presentations/views/components/atoms/Transition';
import Avatar from 'presentations/views/components/atoms/Avatar';
import { useEventTypeListPage } from './hooks';
import {
  gray01,
  gray02,
  gray04,
  primary,
  white,
} from 'presentations/views/styles/color';
import DashboardPageTemplate from 'presentations/views/components/templates/DashboardPageTemplate';
import HostEventTypeList from 'presentations/views/components/organisms/HostEventTypeList';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import DashboardHomeNavigation from 'presentations/views/components/organisms/DashboardHomeNavigation';
import DashboardProjectNavigation from 'presentations/views/components/organisms/DashboardProjectNavigation';
import Headline from 'presentations/views/components/atoms/Headline';
import TextField from 'presentations/views/components/atoms/TextField';
import Checkbox from 'presentations/views/components/atoms/Checkbox';
import Link from 'presentations/views/components/atoms/Link';
import ProductTour from 'presentations/views/components/widgets/ProductTour';
import {
  spacing,
  fontSize,
  mediaMax,
  borderRadius,
  fontWeight,
  isMediaMax,
} from 'presentations/views/styles';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import { ViewerItem } from 'presentations/api/schema/user';
import Pagination from 'presentations/views/components/atoms/Pagination';
import EventTypeWidgetForm from 'presentations/views/components/organisms/EventTypeWidgetForm';
import PlanUpgradeDialog from 'presentations/views/components/organisms/PlanUpgradeDialog';
import EventTypeActivateDialog from 'presentations/views/components/organisms/EventTypeActivateDialog';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import TrialEndDialog from 'presentations/views/components/organisms/TrialEndDialog';
import WelcomeDialog from 'presentations/views/components/organisms/WelcomeDialog';
import { LoadingComponent } from 'presentations/views/components/organisms/EventContent';
import { eventTypeWidgetPath, userPath } from 'presentations/routes/path';
import DateController from 'presentations/views/components/molecules/DateController';
import OnetimeLinkDialog from 'presentations/views/components/organisms/OnetimeLinkDialog';
import { TaskStatus } from 'domains/models/task/status';
import { tasksName } from 'presentations/views/helpers/task';
import DashboardEventList from 'presentations/views/components/organisms/DashboardEventList';
import Skeleton from '../../atoms/Skeleton';

interface Props {
  viewer?: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state: dashboardState } = useContext(DashboardPortalContext);
  const viewer = props.viewer || dashboardState.viewer!;
  const { state, actions } = useEventTypeListPage({
    viewer,
  });
  const isMobile = isMediaMax('tablet');
  const upgradeText = !state.canAddEventType
    ? translate('subscription.plan.eventTypeCountMax')
    : null;
  const isViewer =
    !state.projectUid && viewer.uid === state.currentTeamUser?.userId;

  return (
    <DashboardPageTemplate
      title={t('title')}
      viewer={viewer}
      isWootricEnabled
      showHeaderCalendarError={dashboardState.hasCalendarIntegrationError}
      // isVisibleSupport
      customHeader={
        state.projectUid ? (
          <DashboardProjectNavigation
            activeIndex={0}
            viewer={viewer}
            projectId={state.projectUid}
            onChangeFilter={actions.onChangeFilter}
          />
        ) : (
          <DashboardHomeNavigation
            activeIndex={0}
            viewer={viewer}
            userId={state.currentUserId || viewer.uid}
            onChangeFilter={actions.onChangeFilter}
          />
        )
      }
      tabBarContent={
        state.isEventIntegrating ? (
          <LoadingComponent />
        ) : (
          <>
            <Container>
              <Main>
                <HostEventTypeListContainer>
                  {isViewer && state.allTaskCount !== state.completeTaskCount && (
                    <Transition name="fade" in={true}>
                      <TaskListContainer>
                        <TaskListHeader>
                          <TaskListTitleContainer>
                            <TaskListIcon name="checkbox-marked-outline" />
                            <TaskListTitle>{t('ToDo')}</TaskListTitle>
                            <TaskListCount>
                              {t('Completed Tasks', {
                                complete: state.completeTaskCount,
                                all: state.allTaskCount,
                              })}
                            </TaskListCount>
                          </TaskListTitleContainer>
                          <IconButton onClick={actions.onClickOnboard}>
                            <TaskListIcon
                              name={`chevron-${
                                state.isOpenOnboard ? 'up' : 'down'
                              }`}
                            />
                          </IconButton>
                        </TaskListHeader>
                        {state.isOpenOnboard && (
                          <TaskList>
                            {state.taskList.tasks.map((task) => (
                              <TaskListItem key={task.uid}>
                                <TaskListItemTitle>
                                  <StyledCheckbox
                                    isChecked={task.status === TaskStatus.Done}
                                    onClick={(e: any) =>
                                      actions.taskList.onClickDone(task)
                                    }
                                  />
                                  {tasksName(task)}
                                </TaskListItemTitle>
                                {task.link && (
                                  <StyledLink href={task.link} target="jicoo">
                                    <OpenIcon name="open-in-new" />
                                  </StyledLink>
                                )}
                              </TaskListItem>
                            ))}
                          </TaskList>
                        )}
                      </TaskListContainer>
                    </Transition>
                  )}
                  <FilterContainer>
                    <PageTitleContainer>
                      <PageTitle>{t('Event Type')}</PageTitle>
                    </PageTitleContainer>
                    <SearchTextField
                      name="queryKeyword"
                      size="small"
                      value={state.queryKeyword}
                      prefix={<SearchIcon name="search" />}
                      onChange={actions.onChangeQueryKeyword}
                      placeholder={t('Search placeholder')}
                    />
                  </FilterContainer>
                  <ActionContainer>
                    {state.currentUserId === 'all' ? null : state.project ? (
                      <div />
                    ) : (
                      <HeaderContent>
                        <ProfileImageContainer>
                          {state.currentTeamUser ? (
                            <ProfileAvatar
                              src={state.currentTeamUser.profileImage}
                            >
                              {state.currentTeamUser.name.charAt(0)}
                            </ProfileAvatar>
                          ) : (
                            <Skeleton width="32px" />
                          )}
                        </ProfileImageContainer>
                        <TitleContainer>
                          <Title>
                            {state.currentTeamUser ? (
                              state.currentTeamUser.name
                            ) : (
                              <Skeleton width="300px" />
                            )}
                          </Title>
                          {viewer.team && state.currentTeamUser ? (
                            <UrlLink
                              href={userPath(
                                viewer.team.slug,
                                state.currentTeamUser.slug,
                              )}
                              target="_blank"
                            >
                              <UrlIcon name="open-in-new" />
                              <UrlText>
                                {userPath(
                                  viewer.team.slug,
                                  state.currentTeamUser.slug,
                                )}
                              </UrlText>
                            </UrlLink>
                          ) : (
                            <Skeleton width="200px" />
                          )}
                        </TitleContainer>
                      </HeaderContent>
                    )}
                    {(!!state.projectUid ||
                      state.currentTeamUser?.userId === viewer.uid) && (
                      <ButtonWrap
                        variant="text"
                        size="large"
                        color="primary"
                        onClick={actions.onClickNewEventType}
                        dataProps={{
                          'data-intercom-target': 'event-type-create-button',
                        }}
                      >
                        <ButtonIcon name="plus" />
                        <ButtonText>{t('Create')}</ButtonText>
                      </ButtonWrap>
                    )}
                  </ActionContainer>
                  <HostEventTypeList
                    isLoading={state.isLoading}
                    viewer={viewer}
                    eventTypes={state.eventTypes}
                    onSelectMenu={actions.onSelectMenu}
                    onSelectShareMenu={actions.onSelectShareMenu}
                    onClickShowProfile={actions.onClickShowProfile}
                    canAddEventType={state.canAddEventType}
                  />
                </HostEventTypeListContainer>
                {state.numberOfPages > 1 && (
                  <PaginationContainer>
                    <Pagination
                      currentPage={state.currentPage}
                      numberOfPages={state.numberOfPages}
                      onChange={actions.onChangePage}
                    />
                  </PaginationContainer>
                )}
              </Main>
              {!state.projectUid && (
                <Sub>
                  <BoxHeader>
                    <BoxHeadline variant="h2">
                      {state.isToday ? t("Today's Schedule") : state.dateString}
                    </BoxHeadline>
                    <DateController
                      handleChangeTerm={actions.handleChangeTerm}
                    />
                  </BoxHeader>
                  <EventListContainer>
                    <DashboardEventList
                      viewer={viewer}
                      timeZone={state.timeZone}
                      date={state.date}
                      today={state.today}
                      isFuture={state.isFuture}
                      isToday={state.isToday}
                      setIsEventIntegrating={actions.setIsEventIntegrating}
                      currentUserId={state.currentUserId || viewer.uid}
                      events={state.events}
                      startedAt={state.startedAt}
                      endedAt={state.endedAt}
                      isEventLoading={state.isEventLoading}
                    />
                  </EventListContainer>
                </Sub>
              )}
            </Container>
            <ConfirmationDialog
              isLoading={state.isConfirmLoading}
              isOpen={state.deleteEventType !== null}
              onSubmit={actions.onDeleteEventType}
              onCancel={actions.onCloseDialog}
              title={t('Delete?')}
              submitLabel={t('Delete')}
            />
            <ConfirmationDialog
              isLoading={state.isConfirmLoading}
              isOpen={state.copyEventType !== null}
              onSubmit={actions.onCopyEvent}
              onCancel={actions.onCloseDialog}
              title={t('Copy?')}
            />
            {state.embedEventType && (
              <EventTypeWidgetForm
                embedUrl={eventTypeWidgetPath(state.embedEventType.uid)}
                onClose={actions.onCloseAddToWebsite}
              />
            )}
            {state.isShowingPlanUpgrade && (
              <PlanUpgradeDialog
                text={upgradeText}
                onClose={actions.onClosePlanUpgrade}
              />
            )}
            {state.activateEventType && (
              <EventTypeActivateDialog
                viewer={viewer}
                eventType={state.activateEventType}
                onSubmit={actions.onClickForceActivateEventType}
                onClose={actions.onCloseEventTypeActivate}
              />
            )}
            {isMobile ? (
              <WelcomeDialog
                isOpen={state.isOpenCompleteDialog}
                onClose={actions.onCloseCompleteDialog}
                onSubmit={actions.showEventType}
                submitLabel={t('View EventType')}
                title={t('welcome to jicoo')}
                description={t('Welcome description mobile', {
                  name: viewer.name,
                })}
              />
            ) : (
              <WelcomeDialog
                isOpen={state.isOpenCompleteDialog}
                onClose={actions.onCloseCompleteDialog}
                onSubmit={actions.showProductTour}
                onCancel={actions.showEventType}
                submitLabel={t('View Product Tour')}
                cancelLabel={t('View EventType')}
                title={t('welcome to jicoo')}
                description={t('Welcome description', { name: viewer.name })}
              />
            )}
            {state.isReactivation && (
              <WelcomeDialog
                isOpen
                onClose={actions.onCloseReactivationDialog}
                onSubmit={actions.startReactivate}
                submitLabel={t('View EventType')}
                title={t('Reactivate to jicoo')}
                description={t('Reactivate description', {
                  name: viewer.name,
                  days: 14,
                })}
              />
            )}
            {state.isShowingTrialEnd && (
              <TrialEndDialog onClose={actions.onCloseTrialEnd} />
            )}
            {state.eventTypeForOnetimeLink && (
              <OnetimeLinkDialog
                viewer={viewer}
                eventType={state.eventTypeForOnetimeLink}
                onClose={actions.onCloseOnetimeLinkDialog}
              />
            )}
            {state.isOpenTour && (
              <ProductTour
                isOpen
                onClose={actions.onCloseTourDialog}
                guideItems={state.guideItems}
              />
            )}
            {/* {state.chatPopoverAnchor && (
              <ChatPopover
                activeIndex={ActiveIndex.SummaryChat}
                anchor={state.chatPopoverAnchor}
                viewer={viewer}
                onClose={actions.onCloseChatPopover}
                events={state.events}
                viewerBlocks={[]}
                calendarDateList={[]}
              />
            )} */}
          </>
        )
      }
    />
  );
}

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  padding-bottom: ${spacing(24)};

  ${mediaMax.tablet} {
    flex-direction: column;
  }
`;

const Main = styled.div`
  flex: 1;
  margin-right: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-right: 0;
    width: 100%;
  }
`;
const HostEventTypeListContainer = styled.div`
  margin-top: ${spacing(24)};
  padding-left: ${spacing(24)};

  ${mediaMax.tablet} {
    margin-top: ${spacing(24)};
    padding: 0 ${spacing(12)};
  }
`;
const PageTitleContainer = styled.div``;
const PageTitle = styled.div`
  font-size: ${fontSize(20)};
  font-weight: bold;

  ${mediaMax.tablet} {
    font-size: ${fontSize(16)};
  }
`;
const TitleContainer = styled.div``;
const ProfileImageContainer = styled.div`
  margin-right: ${spacing(8)};
`;
const ProfileAvatar = styled(Avatar)``;
const HeaderContent = styled.div`
  display: flex;
`;
const Title = styled.div`
  font-size: ${fontSize(16)};
  font-weight: bold;

  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const ActionContainer = styled.div`
  margin: 0 auto ${spacing(24)};
  display: flex;
  justify-content: space-between;
  ${mediaMax.tablet} {
    margin: 0 auto ${spacing(16)};
    align-items: center;
    align-items: flex-end;
    /* display: block;
    width: 100%; */
  }
`;
const ButtonWrap = styled(Button)`
  min-height: auto;
  ${mediaMax.tablet} {
  }
`;
const ButtonIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  /* font-size: ${fontSize(16)} !important; */
  width: 16px !important;
  height: 16px !important;

  ${mediaMax.tablet} {
    width: 24px !important;
    height: 24px !important;
  }
`;

const PaginationContainer = styled.div`
  padding: ${spacing(48)} 0;
  display: flex;
  justify-content: center;
`;

const Sub = styled.div`
  position: sticky;
  top: 140px;
  display: flex;
  flex-direction: column;
  width: 320px;
  max-height: calc(100vh - 176px);
  margin: ${spacing(24)} ${spacing(24)} 0 0;
  padding: ${spacing(16)};
  background: ${white};
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};

  ${mediaMax.tablet} {
    display: none;
  }
`;
const BoxHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 ${spacing(16)};
`;
const BoxHeadline = styled(Headline)`
  font-size: ${fontSize(16)};
`;

const ButtonText = styled.span`
  ${mediaMax.smartphone} {
    display: none;
  }
`;

const TaskListContainer = styled.div`
  background-color: ${white};
  padding: ${spacing(8)} ${spacing(16)} ${spacing(16)};
  border-radius: ${borderRadius(16)};
  border: 2px solid ${primary};
  margin-bottom: ${spacing(32)};
`;
const TaskList = styled.div``;
const TaskListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const TaskListTitleContainer = styled.div`
  display: flex;
  align-items: center;
`;
const TaskListTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
  margin-left: ${spacing(8)};
`;
const TaskListCount = styled.span`
  color: ${gray01};
  margin-left: ${spacing(8)};
`;
const TaskListIcon = styled(Icon)`
  color: ${primary};
  width: 21px !important;
  height: 21px !important;
`;
const OpenIcon = styled(Icon)`
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
`;
const EventListContainer = styled.div`
  height: 100%;
  overflow-y: auto;
`;
const TaskListItem = styled.div`
  margin-top: ${spacing(8)};
  display: flex;
  justify-content: space-between;
  :first-of-type {
    margin-top: 0;
    padding-top: ${spacing(12)};
    border-top: 1px solid ${gray04};
  }
  :last-of-type {
    padding-bottom: 0;
  }
`;
const TaskListItemTitle = styled.div`
  display: flex;
  flex: 1;
`;
const StyledCheckbox = styled(Checkbox)`
  height: 1em;
  padding: 0;
  margin-right: ${spacing(8)};
`;
const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
`;
const UrlLink = styled(Link)`
  /* margin-top: ${spacing(4)}; */
  display: flex;
  align-items: center;
`;
const UrlIcon = styled(Icon)`
  margin-right: ${spacing(6)};
  width: 14px !important;
  height: 14px !important;
  color: ${gray02};
`;
const UrlText = styled.span`
  display: inline-block;
  font-size: ${fontSize(13)};
  color: ${gray02};
  ${mediaMax.tablet} {
    word-break: break-all;
  }
`;

const FilterContainer = styled.div`
  margin-bottom: ${spacing(16)};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const SearchIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${gray02};
`;

const SearchTextField = styled(TextField)`
  width: 300px;
  ${mediaMax.tablet} {
    width: 200px;
  }
`;
